/**
 * ========================================
 * 날짜 선택기 Composable (useDatePicker)
 * ========================================
 * Date Picker Composable
 * - 달력 선택 기능을 제공하는 공통 composable
 * - 새로 생성하는 경우 오늘 날짜를 기본값으로 설정
 * - 기존 데이터가 있는 경우 해당 날짜를 표시
 */

import { ref, watch, computed } from 'vue';
import type { Ref, ComputedRef } from 'vue';

/**
 * 날짜 포맷 유틸리티 함수
 * 다양한 날짜 형식을 YYYY-MM-DD 형식으로 변환
 */
export const formatDateForInput = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    
    // 이미 YYYY-MM-DD 형식이면 그대로 반환
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
    }
    
    // ISO 형식 (YYYY-MM-DDTHH:MM:SS.fffZ 또는 YYYY-MM-DDTHH:MM:SSZ) 처리
    // 시간대 변환 없이 날짜 부분만 직접 추출
    const isoMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})T/);
    if (isoMatch) {
        return isoMatch[1]; // 날짜 부분만 추출 (예: "2025-12-18T12:00:00.000Z" → "2025-12-18")
    }
    
    // DD/MM/YYYY 또는 MM/DD/YYYY 형식 처리
    const dateMatch = dateStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (dateMatch) {
        const [, day, month, year] = dateMatch;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // 짧은 문자열(예: "1")은 Date 파싱 시 잘못된 연도로 해석되므로 제외
    if (dateStr.trim().length < 8) {
        return '';
    }

    // Date 객체로 파싱 시도 (마지막 수단)
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
        // 로컬 시간대 기준으로 날짜 추출 (시간대 변환 방지)
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
    
    return '';
};

/**
 * 오늘 날짜를 YYYY-MM-DD 형식으로 반환
 */
export const getTodayDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * YYYY-MM-DD 형식을 dd/mm/yyyy 형식으로 변환
 */
export const formatDateToDDMMYYYY = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    
    // YYYY-MM-DD 형식 파싱
    const dateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateMatch) {
        const [, year, month, day] = dateMatch;
        return `${day}/${month}/${year}`;
    }
    
    // 이미 dd/mm/yyyy 형식인 경우
    const ddmmyyyyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (ddmmyyyyMatch) {
        return dateStr;
    }
    
    // Date 객체로 파싱 시도
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }
    
    return '';
};

/**
 * dd/mm/yyyy 형식을 YYYY-MM-DD 형식으로 변환 (내부 저장용)
 */
export const formatDateFromDDMMYYYY = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    
    // dd/mm/yyyy 형식 파싱 (슬래시 구분자)
    const dateMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dateMatch) {
        const [, day, month, year] = dateMatch;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // 이미 YYYY-MM-DD 형식인 경우
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
    }

    // dd/mm/yyyy 미완성·기타 형식은 파싱하지 않음 (입력 중 값 보존)
    return '';
};

/**
 * v-menu activator props에서 onClick만 제거 (앵커 위치는 유지, 단일 클릭으로 메뉴 미오픈)
 */
export const dateMenuAnchorProps = (
    activatorProps: Record<string, unknown> | undefined
): Record<string, unknown> => {
    if (!activatorProps) return {};
    const { onClick, ...anchorProps } = activatorProps;
    return anchorProps;
};

/**
 * 날짜 선택기 Composable
 * @param initialValue - 초기값 (기존 데이터가 있는 경우)
 * @param defaultToToday - 새로 생성하는 경우 오늘 날짜를 기본값으로 설정할지 여부 (기본값: true)
 * @returns { dateValue, menuOpen, datePickerProps, datePickerEvents }
 * 
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { useDatePicker } from '@/shared/composables/useDatePicker';
 * 
 * const props = defineProps<{
 *   entityDetail?: any;
 * }>();
 * 
 * // 기존 데이터가 있는 경우 해당 날짜 사용, 없으면 오늘 날짜
 * const { dateValue: incorporationDate, menuOpen: incorporationDateMenu } = useDatePicker(
 *   props.entityDetail?.accountingDates?.incorporationDate,
 *   true // 새로 생성하는 경우 오늘 날짜로 설정
 * );
 * </script>
 * 
 * <template>
 *   <v-menu
 *     v-model="incorporationDateMenu"
 *     :close-on-content-click="false"
 *     location="bottom"
 *   >
 *     <template v-slot:activator="{ props }">
 *       <v-text-field
 *         v-bind="props"
 *         v-model="incorporationDate"
 *         label="Incorporation Date"
 *         variant="outlined"
 *         density="comfortable"
 *         placeholder="YYYY-MM-DD"
 *         hide-details
 *         readonly
 *         prepend-inner-icon="mdi-calendar"
 *       />
 *     </template>
 *     <v-date-picker
 *       v-model="incorporationDate"
 *       @update:model-value="incorporationDateMenu = false"
 *     />
 *   </v-menu>
 * </template>
 * ```
 */
export const useDatePicker = (
    initialValue?: string | null | undefined | Ref<string | null | undefined> | (() => string | null | undefined),
    defaultToToday: boolean = true
) => {
    // initialValue가 computed나 ref인 경우 처리
    const getInitialValue = () => {
        if (typeof initialValue === 'function') {
            return initialValue();
        } else if (initialValue && typeof initialValue === 'object' && 'value' in initialValue) {
            return initialValue.value;
        }
        return initialValue;
    };

    // 초기값 처리 - 기존 데이터가 있는 경우만 설정, 없으면 빈 문자열
    let initialDate = '';
    const initial = getInitialValue();
    if (initial) {
        // 기존 데이터가 있는 경우 포맷 변환
        initialDate = formatDateForInput(initial);
    }
    // defaultToToday가 true여도 초기에는 빈 문자열로 설정 (달력 열 때만 오늘 날짜 사용)

    // 반응형 날짜 값 (내부 저장용: YYYY-MM-DD 형식)
    const dateValue = ref<string>(initialDate);
    
    // 표시용 날짜 값 (dd/mm/yyyy 형식)
    const displayValue = computed({
        get: () => formatDateToDDMMYYYY(dateValue.value),
        set: (val: string) => {
            const trimmed = (val || '').trim();
            if (!trimmed) {
                dateValue.value = '';
                return;
            }
            const iso = formatDateFromDDMMYYYY(trimmed);
            if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
                dateValue.value = iso;
            }
        }
    });
    
    // 메뉴 열림/닫힘 상태
    const menuOpen = ref(false);
    
    // 달력에서 사용할 날짜 값 (달력이 열릴 때 설정)
    const calendarDate = ref<string>(initialDate || getTodayDate());

    // 메뉴가 열릴 때 달력 날짜를 설정
    watch(menuOpen, (isOpen) => {
        if (isOpen) {
            // 기존 날짜가 있으면 해당 날짜를 표시, 없으면 오늘 날짜 또는 기본값
            const currentDate = dateValue.value || getInitialValue();
            if (currentDate) {
                calendarDate.value = formatDateForInput(currentDate);
            } else if (defaultToToday) {
                calendarDate.value = getTodayDate();
            } else {
                calendarDate.value = getTodayDate();
            }
        }
    });

    // initialValue가 반응형인 경우 watch 설정
    if (typeof initialValue === 'function' || (initialValue && typeof initialValue === 'object' && 'value' in initialValue)) {
        watch(initialValue as any, (newValue) => {
            // initialValue가 변경되면 dateValue 업데이트
            if (newValue) {
                const formatted = formatDateForInput(newValue);
                if (formatted !== dateValue.value) {
                    dateValue.value = formatted;
                    calendarDate.value = formatted; // calendarDate도 함께 업데이트
                }
            } else {
                // 빈 값인 경우
                dateValue.value = '';
                calendarDate.value = '';
            }
        }, { immediate: true }); // immediate: true로 변경하여 초기 로드 시에도 실행
    } else if (initialValue !== undefined) {
        watch(() => initialValue, (newValue) => {
            // initialValue가 변경되면 dateValue 업데이트
            if (newValue) {
                const formatted = formatDateForInput(newValue);
                if (formatted !== dateValue.value) {
                    dateValue.value = formatted;
                    calendarDate.value = formatted; // calendarDate도 함께 업데이트
                }
            } else {
                // 빈 값인 경우
                dateValue.value = '';
                calendarDate.value = '';
            }
        }, { immediate: true }); // immediate: true로 변경
    }

    return {
        dateValue, // 내부 저장용 (YYYY-MM-DD)
        displayValue, // 표시용 (dd/mm/yyyy)
        menuOpen,
        calendarDate,
    };
};

export default {
    useDatePicker,
    formatDateForInput,
    formatDateToDDMMYYYY,
    formatDateFromDDMMYYYY,
    getTodayDate,
    dateMenuAnchorProps,
};

